import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addAccountNote,
  addLeadNote,
  commitApolloLeadImport,
  enrichApolloPerson,
  fetchApolloEmailAccounts,
  fetchApolloLists,
  fetchApolloPeopleSearchFilterOptions,
  fetchApolloSequenceDetail,
  fetchApolloSequences,
  fetchApolloTemplates,
  fetchApolloUsers,
  fetchSettings,
  testApolloConnection,
  commitLeadImport,
  convertLead,
  createCall,
  createAccount,
  createLead,
  fetchAccount,
  fetchAccounts,
  fetchActivities,
  fetchCallDetail,
  fetchCallSession,
  fetchCalls,
  fetchDashboardSummary,
  fetchFollowUps,
  fetchFounderInsightsOverview,
  fetchHotLeads,
  fetchLead,
  fetchLeads,
  fetchOpportunities,
  fetchUsers,
  approveLeadEmail,
  fetchLeadAIPanel,
  fetchWorkQueue,
  previewApolloLeadImport,
  previewLeadImport,
  qualifyLead,
  reEnrichLead,
  searchApolloPeople,
  enrollLeadInApolloSequence,
  stopLeadInApolloSequence,
  syncLeadToApollo,
  updateSettings,
  updateAccount,
  updateLead,
} from '@api/crm';
import type {
  CRMApolloConnectionTestRequest,
  CRMApolloEnrollRequest,
  CRMApolloProspectEnrichmentRequest,
  CRMApolloLeadImportCommitRequest,
  CRMApolloLeadImportPreviewRequest,
  CRMApolloPeopleSearchRequest,
  CRMAccountPayload,
  CRMApproveEmailRequest,
  CRMCallCreatePayload,
  CRMLeadConvertPayload,
  CRMLeadImportPreviewRequest,
  CRMLeadPayload,
  CRMLeadQualifyPayload,
  CRMNotePayload,
  CRMSettingUpdatePayload,
} from '@/types/api';

function invalidateCoreCRM(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['crm', 'accounts'] }),
    queryClient.invalidateQueries({ queryKey: ['crm', 'leads'] }),
    queryClient.invalidateQueries({ queryKey: ['crm', 'opportunities'] }),
    queryClient.invalidateQueries({ queryKey: ['crm', 'dashboard'] }),
    queryClient.invalidateQueries({ queryKey: ['crm', 'hot-leads'] }),
    queryClient.invalidateQueries({ queryKey: ['crm', 'follow-ups'] }),
    queryClient.invalidateQueries({ queryKey: ['crm', 'calls'] }),
  ]);
}

export function useCRMDashboard() {
  return useQuery({
    queryKey: ['crm', 'dashboard'],
    queryFn: fetchDashboardSummary,
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ['crm', 'users'],
    queryFn: fetchUsers,
  });
}

export function useHotLeads(ownerUserId?: string) {
  return useQuery({
    queryKey: ['crm', 'hot-leads', ownerUserId ?? ''],
    queryFn: () => fetchHotLeads(ownerUserId),
  });
}

export function useFollowUps(ownerUserId?: string) {
  return useQuery({
    queryKey: ['crm', 'follow-ups', ownerUserId ?? ''],
    queryFn: () => fetchFollowUps(ownerUserId),
  });
}

export function useAccounts(search?: string, status?: string) {
  return useQuery({
    queryKey: ['crm', 'accounts', search ?? '', status ?? ''],
    queryFn: () => fetchAccounts(search, status),
  });
}

export function useAccount(accountId?: string) {
  return useQuery({
    queryKey: ['crm', 'account', accountId ?? ''],
    queryFn: () => fetchAccount(accountId!),
    enabled: Boolean(accountId),
  });
}

export function useAccountTimeline(accountId?: string) {
  return useQuery({
    queryKey: ['crm', 'activities', 'account', accountId ?? ''],
    queryFn: () => fetchActivities({ accountId: accountId! }),
    enabled: Boolean(accountId),
  });
}

export function useCallSession() {
  return useQuery({
    queryKey: ['crm', 'call-session'],
    queryFn: () => fetchCallSession(),
  });
}

export function useCalls(filters: { accountId?: string; leadId?: string; contactId?: string; opportunityId?: string; ownerUserId?: string; status?: string }) {
  return useQuery({
    queryKey: ['crm', 'calls', filters],
    queryFn: () => fetchCalls(filters),
    enabled: Boolean(filters.accountId || filters.leadId || filters.contactId || filters.opportunityId || filters.ownerUserId),
  });
}

export function useCallDetail(callId?: string) {
  return useQuery({
    queryKey: ['crm', 'call', callId ?? ''],
    queryFn: () => fetchCallDetail(callId!),
    enabled: Boolean(callId),
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CRMAccountPayload) => createAccount(payload),
    onSuccess: async (account) => {
      await invalidateCoreCRM(queryClient);
      queryClient.setQueryData(['crm', 'account', account.id], account);
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, payload }: { accountId: string; payload: Partial<CRMAccountPayload> }) =>
      updateAccount(accountId, payload),
    onSuccess: async (account) => {
      await invalidateCoreCRM(queryClient);
      queryClient.setQueryData(['crm', 'account', account.id], account);
    },
  });
}

export function useAddAccountNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, payload }: { accountId: string; payload: CRMNotePayload }) =>
      addAccountNote(accountId, payload),
    onSuccess: async (_activity, variables) => {
      await Promise.all([
        invalidateCoreCRM(queryClient),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'account', variables.accountId] }),
      ]);
    },
  });
}

export function useCreateCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CRMCallCreatePayload) => createCall(payload),
    onSuccess: async (call) => {
      await Promise.all([
        invalidateCoreCRM(queryClient),
        queryClient.invalidateQueries({ queryKey: ['crm', 'call-session'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'call', call.id] }),
      ]);
      if (call.lead_id) {
        await queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'lead', call.lead_id] });
      }
      if (call.account_id) {
        await queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'account', call.account_id] });
      }
    },
  });
}

export function useLeads(search?: string, status?: string, limit?: number) {
  return useQuery({
    queryKey: ['crm', 'leads', search ?? '', status ?? '', limit ?? 40],
    queryFn: () => fetchLeads(search, status, limit),
  });
}

export function useLead(leadId?: string) {
  return useQuery({
    queryKey: ['crm', 'lead', leadId ?? ''],
    queryFn: () => fetchLead(leadId!),
    enabled: Boolean(leadId),
  });
}

export function useLeadTimeline(leadId?: string) {
  return useQuery({
    queryKey: ['crm', 'activities', 'lead', leadId ?? ''],
    queryFn: () => fetchActivities({ leadId: leadId! }),
    enabled: Boolean(leadId),
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CRMLeadPayload) => createLead(payload),
    onSuccess: async (lead) => {
      await invalidateCoreCRM(queryClient);
      queryClient.setQueryData(['crm', 'lead', lead.id], lead);
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: Partial<CRMLeadPayload> }) => updateLead(leadId, payload),
    onSuccess: async (lead) => {
      await invalidateCoreCRM(queryClient);
      queryClient.setQueryData(['crm', 'lead', lead.id], lead);
    },
  });
}

export function useQualifyLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: CRMLeadQualifyPayload }) => qualifyLead(leadId, payload),
    onSuccess: async (lead) => {
      await invalidateCoreCRM(queryClient);
      queryClient.setQueryData(['crm', 'lead', lead.id], lead);
    },
  });
}

export function useConvertLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: CRMLeadConvertPayload }) => convertLead(leadId, payload),
    onSuccess: async (response) => {
      await invalidateCoreCRM(queryClient);
      queryClient.setQueryData(['crm', 'lead', response.lead.id], response.lead);
    },
  });
}

export function useAddLeadNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: CRMNotePayload }) => addLeadNote(leadId, payload),
    onSuccess: async (_activity, variables) => {
      await Promise.all([
        invalidateCoreCRM(queryClient),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'lead', variables.leadId] }),
      ]);
    },
  });
}

export function useLeadImportPreview() {
  return useMutation({
    mutationFn: (payload: CRMLeadImportPreviewRequest) => previewLeadImport(payload),
  });
}

export function useLeadImportCommit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CRMLeadImportPreviewRequest) => commitLeadImport(payload),
    onSuccess: async () => {
      await invalidateCoreCRM(queryClient);
    },
  });
}

export function useOpportunities(stage?: string) {
  return useQuery({
    queryKey: ['crm', 'opportunities', stage ?? ''],
    queryFn: () => fetchOpportunities(stage),
  });
}

export function useCRMSettings() {
  return useQuery({
    queryKey: ['crm', 'settings'],
    queryFn: fetchSettings,
  });
}

export function useUpdateCRMSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CRMSettingUpdatePayload) => updateSettings(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm', 'settings'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'apollo-sequences'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'apollo-email-accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'apollo-users'] }),
      ]);
    },
  });
}

export function useApolloConnectionTest() {
  return useMutation({
    mutationFn: (payload?: CRMApolloConnectionTestRequest) => testApolloConnection(payload),
  });
}

export function useApolloSequences(search?: string, enabled = true) {
  return useQuery({
    queryKey: ['crm', 'apollo-sequences', search ?? ''],
    queryFn: () => fetchApolloSequences(search),
    enabled,
  });
}

export function useApolloSequenceDetail(sequenceId?: string, enabled = true) {
  return useQuery({
    queryKey: ['crm', 'apollo-sequence-detail', sequenceId ?? ''],
    queryFn: () => fetchApolloSequenceDetail(sequenceId!),
    enabled: enabled && Boolean(sequenceId),
  });
}

export function useApolloTemplates(search?: string, enabled = true) {
  return useQuery({
    queryKey: ['crm', 'apollo-templates', search ?? ''],
    queryFn: () => fetchApolloTemplates(search),
    enabled,
  });
}

export function useApolloEmailAccounts(enabled = true) {
  return useQuery({
    queryKey: ['crm', 'apollo-email-accounts'],
    queryFn: fetchApolloEmailAccounts,
    enabled,
  });
}

export function useApolloUsers(enabled = true) {
  return useQuery({
    queryKey: ['crm', 'apollo-users'],
    queryFn: fetchApolloUsers,
    enabled,
  });
}

export function useApolloLists(enabled = true) {
  return useQuery({
    queryKey: ['crm', 'apollo-lists'],
    queryFn: fetchApolloLists,
    enabled,
  });
}

export function useApolloPeopleSearch() {
  return useMutation({
    mutationFn: (payload: CRMApolloPeopleSearchRequest) => searchApolloPeople(payload),
  });
}

export function useApolloPeopleSearchFilterOptions(qKeywords?: string, enabled = true) {
  return useQuery({
    queryKey: ['crm', 'apollo-people-filter-options', qKeywords ?? ''],
    queryFn: () => fetchApolloPeopleSearchFilterOptions(qKeywords),
    enabled,
  });
}

export function useApolloPersonEnrichment() {
  return useMutation({
    mutationFn: (payload: CRMApolloProspectEnrichmentRequest) => enrichApolloPerson(payload),
  });
}

export function useApolloLeadImportPreview() {
  return useMutation({
    mutationFn: (payload: CRMApolloLeadImportPreviewRequest) => previewApolloLeadImport(payload),
  });
}

export function useApolloLeadImportCommit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CRMApolloLeadImportCommitRequest) => commitApolloLeadImport(payload),
    onSuccess: async (response) => {
      await invalidateCoreCRM(queryClient);
      for (const lead of response.leads) {
        queryClient.setQueryData(['crm', 'lead', lead.id], lead);
      }
    },
  });
}

export function useSyncLeadToApollo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => syncLeadToApollo(leadId),
    onSuccess: async (_response, leadId) => {
      await Promise.all([
        invalidateCoreCRM(queryClient),
        queryClient.invalidateQueries({ queryKey: ['crm', 'lead', leadId] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'lead', leadId] }),
      ]);
    },
  });
}

export function useEnrollLeadInApolloSequence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: CRMApolloEnrollRequest }) =>
      enrollLeadInApolloSequence(leadId, payload),
    onSuccess: async (_response, { leadId }) => {
      await Promise.all([
        invalidateCoreCRM(queryClient),
        queryClient.invalidateQueries({ queryKey: ['crm', 'apollo-sequence-detail'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'lead', leadId] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'lead', leadId] }),
      ]);
    },
  });
}

export function useStopLeadInApolloSequence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => stopLeadInApolloSequence(leadId),
    onSuccess: async (_response, leadId) => {
      await Promise.all([
        invalidateCoreCRM(queryClient),
        queryClient.invalidateQueries({ queryKey: ['crm', 'apollo-sequence-detail'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'lead', leadId] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'lead', leadId] }),
      ]);
    },
  });
}

export function useLeadAIPanel(leadId: string | undefined) {
  return useQuery({
    queryKey: ['crm', 'ai-panel', leadId],
    queryFn: () => fetchLeadAIPanel(leadId!),
    enabled: Boolean(leadId),
    refetchInterval: (query) => {
      const status = query.state.data?.ai_enrichment_status;
      return status === 'pending' ? 3000 : false;
    },
  });
}

export function useWorkQueue() {
  return useQuery({
    queryKey: ['crm', 'work-queue'],
    queryFn: fetchWorkQueue,
    staleTime: 5 * 60 * 1000, // treat as fresh for 5 minutes
  });
}

export function useFounderInsights() {
  return useQuery({
    queryKey: ['founder', 'insights', 'overview'],
    queryFn: fetchFounderInsightsOverview,
    staleTime: 60 * 1000,
  });
}

export function useReEnrichLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => reEnrichLead(leadId),
    onSuccess: (_data, leadId) => {
      queryClient.invalidateQueries({ queryKey: ['crm', 'ai-panel', leadId] });
    },
  });
}

export function useApproveEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: CRMApproveEmailRequest }) =>
      approveLeadEmail(leadId, payload),
    onSuccess: async (_data, { leadId }) => {
      await queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'lead', leadId] });
    },
  });
}
